// pages/FormViewerPage/hooks/useFormViewer/submitCapi.ts — CAPI events fire-and-forget
// SCOPE (fix 2026-08-24): event Purchase cuma di-fire ke pixel yang SAMA dgn browser
// pixel halaman (buildThankYouPixelConfigs). Sebelumnya dikirim ke SEMUA pixel global
// → action_source=website tanpa pasangan browser pixel → Meta anggap data inconsistent
// → domain diblokir. Sekarang tidak ada lagi cross-pixel pollution.
// TRIGGER (2026-08-25, revisi 2026-10-20): trigger per-pixel di /pengaturan/pelacakan
// HANYA mengatur Purchase (closing). Lead (order baru Pending) SELALU fire via CAPI
// saat submit sebagai backup browser pixel (dedup otomatis: event_id = orderId sama
// di kedua jalur). Browser-only = 1 titik gagal (kasus Viral.Ltd: checkout tercatat,
// Lead nol). Purchase: 'created' = legacy (tidak pernah via sweep); sisanya di-stash
// (/api/capi/stash) dan sweeper server fire saat status order sampai trigger.
import { buildThankYouPixelConfigs } from '../../../ThankYouPage/utils/pixelConfigs';
import { getStoredTtclid, getTtpCookie } from '../../../../services/attributionIds';
import { stashCapiSignals } from './capiStash';
import { getOpenAIAttribution, sourceUrl } from '../../../../services/openaiAttribution';

export function fireCapiEvents(params: {
    globalTrackingSettings: any;
    form: any;
    phone: string;
    email: string;
    name: string;
    sellingPrice: number;
    shippingCost: number;
    codFee: number;
    productName: string;
    orderData: any;
    orderValue?: number;
    customerCity?: string;
    customerProvince?: string;
    customerPostalCode?: string;
}) {
    const { globalTrackingSettings, form, phone, email, name, sellingPrice, shippingCost, codFee, productName, orderData, orderValue: orderValueOverride, customerCity, customerProvince, customerPostalCode } = params;
    (async () => {
        try {
            const fbc = (() => { try { const u = new URLSearchParams(window.location.search), f = u.get('fbclid'); if (f) return `fb.1.${Date.now()}.${f}`; const m = document.cookie.match(/(?:^|;\s*)_fbc=([^;]*)/); return m ? decodeURIComponent(m[1]) : undefined } catch { return undefined } })();
            const fbp = (() => { try { const m = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/); return m ? decodeURIComponent(m[1]) : undefined } catch { return undefined } })();
            // PII dikirim RAW — backend yang normalisasi+hash (satu sumber kebenaran,
            // anti double-hash & anti mismatch 0→62). Sinyal TikTok utk match/CAPI.
            const sig = { ttclid: getStoredTtclid(), ttp: getTtpCookie(), externalId: orderData.id, fbc, fbp };
            const orderValue = orderValueOverride ?? (sellingPrice + shippingCost + codFee);
            const eventId = orderData.id;
            const pageUrl = sourceUrl();
            const { oppref, obref } = getOpenAIAttribution();

            // Pixel yang benar-benar terpasang di halaman ini (pixel meta), bukan global loop.
            const configs = buildThankYouPixelConfigs(form, globalTrackingSettings);
            const idsFor = (platform: string) =>
                (configs.find(c => c.platform === platform)?.pixelIds || []).filter((id: string) => id?.trim());
            // Thanks page form fisik = Lead (order baru Pending). Event CAPI ikuti dropdown
            // thankYou (default Lead); Purchase hanya via sweep saat closing (processing/delivered).
            const eventFor = (platform: string) =>
                configs.find(c => c.platform === platform)?.eventName || 'Lead';

            // Trigger PER-PIXEL (default 'processing') HANYA tentukan stash Purchase
            // (/api/capi/stash → sweep fire saat status sampai). Lead SELALU fire
            // langsung di bawah — tidak digate trigger.
            const triggerFor = (platform: string, pixelId: string) => {
                const ps = (globalTrackingSettings as any)?.[platform];
                const pix = (ps?.pixels || []).find((p: any) => p.id === pixelId);
                return pix?.trigger || 'processing';
            };

            const capiPlatforms = ['meta', 'tiktok', 'google', 'openai'] as const;
            const stashPixels: Record<string, string[]> = { meta: [], tiktok: [], google: [], openai: [] };
            let needStash = false;
            // Event ikuti dropdown thankYou (default Lead — thanks = order Pending,
            // bukan closing). Server mapping: TikTok Lead→SubmitForm, Google Lead→generate_lead.
            const piiRaw = { customerPhone: phone, customerEmail: email, customerName: name, customerCity, customerProvince, customerPostalCode, customerCountry: 'ID' };
            for (const platform of capiPlatforms) {
                const pixelIds = idsFor(platform);
                const capiEvent = eventFor(platform);
                for (const pixelId of pixelIds) {
                    // Trigger pelacakan HANYA tentukan stash Purchase — Lead SELALU fire.
                    if (triggerFor(platform, pixelId) !== 'created') {
                        stashPixels[platform].push(pixelId);
                        needStash = true;
                    }
                    let endpoint: string;
                    let payload: Record<string, any>;
                    if (platform === 'meta') {
                        endpoint = '/api/capi/meta';
                        payload = { eventName: capiEvent, pixelId, eventId, value: orderValue, currency: 'IDR', contentName: productName, orderId: orderData.id, ...piiRaw, ...sig, pageUrl };
                    } else if (platform === 'tiktok') {
                        endpoint = '/api/capi/tiktok';
                        payload = { eventName: capiEvent, pixelId, value: orderValue, currency: 'IDR', contentName: productName, orderId: orderData.id, ...piiRaw, ...sig, pageUrl };
                    } else if (platform === 'google') {
                        endpoint = '/api/capi/google';
                        payload = { eventName: capiEvent, pixelId, clientId: pixelId, value: orderValue, currency: 'IDR', contentName: productName, orderId: orderData.id, ...piiRaw, ...sig, pageUrl };
                    } else {
                        endpoint = '/api/capi/openai';
                        payload = { eventName: capiEvent, pixelId, eventId, value: orderValue, currency: 'IDR', contentName: productName, orderId: orderData.id, ...piiRaw, pageUrl, oppref, obref };
                    }
                    fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).catch(() => {});
                }
            }

            // Simpan sinyal browser utk fire tertunda (trigger processing/delivered).
            if (needStash) {
                stashCapiSignals({ orderId: orderData.id, fbc, fbp, pageUrl, ttclid: sig.ttclid, ttp: sig.ttp, externalId: sig.externalId, metaPixelIds: stashPixels.meta, tiktokPixelIds: stashPixels.tiktok, googlePixelIds: stashPixels.google, openaiPixelIds: stashPixels.openai, oppref, obref });
            }
            try { sessionStorage.setItem(`capi_fired_${orderData.id}`, '1'); } catch { /* */ }
        } catch { /* */ }
    })();
}
